import * as React from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import {
  ContractVersionsPageDocument,
  PauseContractsContractDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import FirstStep from './FirstStep'

interface Props {
  contractId: string
  employeeId: string
  lastEffectiveOnDate: Date
  setSelectedContractVersion: (contractVersionId: string | undefined) => void
  onClose: () => void
}

const PauseContractModal = ({
  contractId,
  employeeId,
  lastEffectiveOnDate,
  setSelectedContractVersion,
  onClose,
}: Props) => {
  const [isLoading, setIsLoading] = React.useState(false)
  const [activityEndDate, setActivityEndDate] = React.useState('')
  const [employeeTerminationDate, setEmployeeTerminationDate] = React.useState('')
  const [managerId, setManagerId] = React.useState<string | null>(null)
  const apolloClient = useApolloClient()
  const { company } = useCurrentSession()

  // Handle activity end date changes with validation logic
  const handleActivityEndDateChange = (value: string) => {
    setActivityEndDate(value)

    // Reset employee termination date when activity end date changes
    setEmployeeTerminationDate(value)
  }

  // Calculate min and max dates for employee termination date
  const getEmployeeTerminationDateConstraints = () => {
    if (!activityEndDate) {
      return { minDate: undefined, maxDate: undefined, readonly: true }
    }

    const activityDate = stringToDate(activityEndDate, 'ui')
    if (!activityDate) {
      return { minDate: undefined, maxDate: undefined, readonly: true }
    }

    // Employee termination date can only be a later date than the activity end date
    const minDate = activityDate.toDate()
    // Employee termination date can only be 7 days after the activity end date
    const maxDate = new Date(activityDate.toDate())
    maxDate.setDate(maxDate.getDate() + 7)

    return { minDate, maxDate, readonly: false }
  }

  const employeeTerminationConstraints = getEmployeeTerminationDateConstraints()

  const [pauseContract] = useMutation(PauseContractsContractDocument, {
    extractorFn: (data) => data.contractsMutations.pauseContractsContract,
  })

  const handleSubmit = async () => {
    setIsLoading(true)

    try {
      const activityDate = stringToDate(activityEndDate, 'ui')
      const backendDateString = activityDate ? i18nDateToString(activityDate, 'backend') : null

      const termDate = stringToDate(employeeTerminationDate, 'ui')
      const terminationBackendDateString = termDate ? i18nDateToString(termDate, 'backend') : null

      if (!backendDateString) {
        throw new Error('Invalid date selected')
      }

      const result = await pauseContract({
        variables: {
          contractId,
          contractActivityPauseOn: backendDateString,
          employeeTerminatedOn: terminationBackendDateString || backendDateString,
          reassignToEmployeeId: managerId,
        },
      })

      const handled = handleResult(result, (_errors) => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('contract_versions.header.pause_resume.pause_modal.error'),
          time: 3000,
        })
      })

      if (handled.success) {
        // Manually refetch the ContractVersionsPage query to ensure UI updates
        await apolloClient.refetchQueries({
          include: [ContractVersionsPageDocument],
        })

        app.setFlash({
          type: 'correct',
          message: i18n.t('contract_versions.header.pause_resume.pause_modal.success'),
          time: 3000,
        })
        // Setting to undefined forces a repaint and sets the reference contract version as selected version
        track(trackingEvents.employee.contracts.endActivityPeriodSubmit, {
          end_date: activityEndDate,
          company_id: company?.id,
        })
        setSelectedContractVersion(undefined)
        onClose()
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('contract_versions.header.pause_resume.pause_modal.error'),
        time: 3000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      title={i18n.t('contract_versions.header.pause_resume.pause_modal.title')}
      description={i18n.t('contract_versions.header.pause_resume.pause_modal.description')}
      illustration="time-planning"
      onClose={onClose}
      onSubmit={handleSubmit}
      disableFocusTrap
      footer={
        <>
          <Button type="secondary" onClick={onClose} disabled={isLoading}>
            {i18n.t('common.cancel')}
          </Button>
          <Button
            type="primary"
            disabled={!activityEndDate || isLoading}
            submit
            loading={isLoading}
          >
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      {isLoading ? (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
          }}
        >
          <Spinner />
        </Box>
      ) : (
        <FirstStep
          employeeId={employeeId}
          activityEndDate={activityEndDate}
          setActivityEndDate={handleActivityEndDateChange}
          employeeTerminationDate={employeeTerminationDate}
          setEmployeeTerminationDate={setEmployeeTerminationDate}
          managerId={managerId}
          setManagerId={setManagerId}
          employeeTerminationConstraints={employeeTerminationConstraints}
          lastEffectiveOnDate={lastEffectiveOnDate}
        />
      )}
    </Modal>
  )
}

export default PauseContractModal
